/**
 * 参数快照：按「机型 + 固件版本 + 槽位」存档，用于"与原厂默认对比"和"上次的样子是什么"。
 *
 * ## 为什么用分片，以及为什么是这个片大小
 *
 * 一个机型的全量参数在 1000～1500 条（ArduCopter 4.5 全量约 1100 条，带云台/RTK 的机架更多）。
 * 每条以 `{ name, value }` 的结构化克隆存放：名字平均 12 个字符（UTF-8 12 字节）+ 数字 8 字节
 * + 对象与字段开销约 16 字节 ≈ **40 字节/条**，于是 1000 条约 **40 KB**，1500 条约 **60 KB**。
 *
 * 分片大小取 **256 条/片**（`PARAM_SNAPSHOT_CHUNK_SIZE`）：
 *
 * - 单片约 10 KB，1500 条参数 = 1 条头部 + 6 条片记录。单条记录压在 16 KB 以内，
 *   写入失败时的粒度就是"一片"，而不是"整份快照"。
 * - 分片带来的**真实**收益主要是"只读头部"：列表页要显示"上次下载是 4.5.6 固件、1032 条参数、
 *   什么时候"，这是头部一条记录（10 KB 以内的一次 `get`），不需要解码全部参数。
 *   对比之下，把 60 KB 全读回来只为显示一行摘要，在参数页反复打开时是纯浪费。
 * - **没有**做二进制打包。打包（名字表 + Float32 数组）能把体积压到约 16 字节/条，
 *   比 40 字节小 2.5 倍。代价是要引入自定义格式、自写解码器、以及"格式版本"这第三个版本号，
 *   而 60 KB 的绝对值并不值得 —— 配额压力来自审计日志与地图缓存，不是参数快照。
 *   换句话说：**用 2.5 倍的体积换"不需要自定义解码器"，在 60 KB 这个量级上是划算的**。
 * - 也没有用 JSON 字符串：结构化克隆原生支持 `Uint8Array`（参数值里可能有二进制来源），
 *   且省掉 `JSON.parse` / `JSON.stringify` 往返与 UTF-8 编解码。
 *
 * ## 原子性
 *
 * 头部与所有分片在**同一个 `batch()`** 里写，并顺手删掉比新快照更长的旧分片。
 * 不删旧分片会让"上上次那份 1500 条的快照"的尾巴留在库里，而头部已经被新的 1000 条覆盖 ——
 * 读出来的参数集就是拼错的，而且不会有任何报错。
 */

import {
  NotStorableError,
  runSave,
  saveOk,
  type SaveOk,
  type SaveOutcome,
  type StorageFailure,
} from './errors';
import {
  isReadableFormat,
  isRecordLike,
  paramSnapshotChunkKey,
  paramSnapshotSlotPrefix,
  parseParamSnapshotKey,
  readFiniteNumber,
  readOptionalString,
  readString,
  PARAM_SNAPSHOT_PREFIX,
  RECORD_FORMAT,
  type ParamSnapshotSlot,
} from './keys';
import { estimateStorableBytes } from './serialization';
import type { KeyValueStore, StoreOperation } from './store';

/** 每片的参数条数，依据见文件头 */
export const PARAM_SNAPSHOT_CHUNK_SIZE = 256;

/** 这份参数是怎么来的 */
export type ParamSnapshotSource = 'download' | 'import' | 'factory-default';

/** 一条参数。名字沿用 ArduPilot 的 `PARAM_NAME`（最多 16 字节），值按 `float` 存 */
export interface ParamRecord {
  readonly name: string;
  readonly value: number;
}

/** 快照头部；存在该槽位的第 0 片（`.../000000`） */
export interface ParamSnapshotHeader {
  readonly format: number;
  readonly kind: 'header';
  readonly model: string;
  readonly firmware: string;
  readonly slot: ParamSnapshotSlot;
  readonly source: ParamSnapshotSource;
  readonly capturedAt: number;
  readonly paramCount: number;
  readonly chunkSize: number;
  /** 参数片数，不含头部 */
  readonly chunkCount: number;
  /**
   * 这份快照（头部 + 全部分片）的估算占用字节。
   * 写在头部里而不是每次现算：现算要把全部分片读一遍，而列表与"上次的样子"摘要
   * 本来只需要一条头部记录 —— 那正是分片的意义所在，别自己把它毁掉。
   */
  readonly bytes: number;
  readonly vehicleLabel?: string;
  readonly notes?: string;
}

/** 参数分片；第 1..N 片 */
export interface ParamSnapshotChunk {
  readonly format: number;
  readonly kind: 'chunk';
  readonly index: number;
  readonly params: readonly ParamRecord[];
}

export type ParamSnapshotChunkRecord = ParamSnapshotHeader | ParamSnapshotChunk;

export interface ParamSnapshotSummary {
  readonly model: string;
  readonly firmware: string;
  readonly slot: ParamSnapshotSlot;
  readonly source: ParamSnapshotSource;
  readonly capturedAt: number;
  readonly paramCount: number;
  readonly chunkSize: number;
  readonly chunkCount: number;
  /** 估算占用字节，用于界面显示与清理建议 */
  readonly bytes: number;
  readonly vehicleLabel?: string;
  readonly notes?: string;
}

export interface ParamSnapshot {
  readonly header: ParamSnapshotHeader;
  readonly params: readonly ParamRecord[];
}

export interface BuildParamSnapshotOptions {
  readonly model: string;
  readonly firmware: string;
  readonly params: readonly ParamRecord[];
  readonly slot?: ParamSnapshotSlot;
  readonly source?: ParamSnapshotSource;
  readonly capturedAt: number;
  readonly vehicleLabel?: string;
  readonly notes?: string;
  readonly chunkSize?: number;
}

export interface BuiltParamSnapshot {
  readonly header: ParamSnapshotHeader;
  readonly chunks: readonly ParamSnapshotChunk[];
  readonly bytes: number;
}

function isParamName(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 16;
}

/** 校验一条参数；参数名是数据格式的一部分（16 字节上限来自 MAVLink 的 `PARAM_NAME`） */
export function isParamRecord(value: unknown): value is ParamRecord {
  if (!isRecordLike(value)) return false;
  return isParamName(value['name']) && typeof value['value'] === 'number';
}

/**
 * 按分片规则生成一份快照的全部记录。
 * 迁移步骤也会用它把旧格式（内联全部参数）改写成新格式，因此这里是"快照长什么样"的唯一定义。
 */
export function buildParamSnapshot(options: BuildParamSnapshotOptions): BuiltParamSnapshot {
  const chunkSize = options.chunkSize ?? PARAM_SNAPSHOT_CHUNK_SIZE;
  if (!Number.isSafeInteger(chunkSize) || chunkSize <= 0) {
    throw new RangeError(`分片大小必须是正整数，收到 ${chunkSize}`);
  }
  const { model, firmware, params } = options;
  if (model.length === 0) throw new RangeError('机型不能为空：快照按机型隔离，空机型会让所有机型互相覆盖');
  const slot = options.slot ?? 'last';
  const source = options.source ?? (slot === 'factory-default' ? 'factory-default' : 'download');

  const chunks: ParamSnapshotChunk[] = [];
  for (let offset = 0, index = 1; offset < params.length; offset += chunkSize, index += 1) {
    chunks.push({
      format: RECORD_FORMAT,
      kind: 'chunk',
      index,
      params: params.slice(offset, offset + chunkSize),
    });
  }

  // 头部自身的开销（机型/固件/时间戳/若干数字字段）按 160 字节估，分片按实际估算累加
  const bytes = chunks.reduce((sum, chunk) => sum + estimateStorableBytes(chunk), 0) + 160;

  const header: ParamSnapshotHeader = {
    format: RECORD_FORMAT,
    kind: 'header',
    model,
    firmware,
    slot,
    source,
    capturedAt: options.capturedAt,
    paramCount: params.length,
    chunkSize,
    chunkCount: chunks.length,
    bytes,
    ...(options.vehicleLabel === undefined ? {} : { vehicleLabel: options.vehicleLabel }),
    ...(options.notes === undefined ? {} : { notes: options.notes }),
  };

  return { header, chunks, bytes };
}

/** 读取校验：不认识的结构返回 null，由调用方决定是"跳过并报告"还是当作"没有" */
export function readParamSnapshotRecord(value: unknown): ParamSnapshotChunkRecord | null {
  if (!isRecordLike(value)) return null;
  if (!isReadableFormat(value['format'])) return null;
  const kind = readString(value['kind']);
  if (kind === 'header') {
    const model = readString(value['model']);
    const firmware = readString(value['firmware']);
    const slot = readString(value['slot']);
    const source = readString(value['source']);
    const capturedAt = readFiniteNumber(value['capturedAt']);
    const paramCount = readFiniteNumber(value['paramCount']);
    const chunkSize = readFiniteNumber(value['chunkSize']);
    const chunkCount = readFiniteNumber(value['chunkCount']);
    if (model === null || firmware === null || capturedAt === null) return null;
    if (slot !== 'last' && slot !== 'factory-default') return null;
    if (source !== 'download' && source !== 'import' && source !== 'factory-default') return null;
    if (paramCount === null || chunkSize === null || chunkCount === null) return null;
    const vehicleLabel = readOptionalString(value['vehicleLabel']);
    const notes = readOptionalString(value['notes']);
    return {
      format: RECORD_FORMAT,
      kind: 'header',
      model,
      firmware,
      slot,
      source,
      capturedAt,
      paramCount,
      chunkSize,
      chunkCount,
      bytes: readFiniteNumber(value['bytes']) ?? 0,
      ...(vehicleLabel === undefined ? {} : { vehicleLabel }),
      ...(notes === undefined ? {} : { notes }),
    };
  }
  if (kind === 'chunk') {
    const index = readFiniteNumber(value['index']);
    if (index === null) return null;
    const raw = value['params'];
    if (!Array.isArray(raw)) return null;
    const params: ParamRecord[] = [];
    for (const entry of raw) {
      if (!isParamRecord(entry)) return null;
      params.push({ name: entry.name, value: entry.value });
    }
    return { format: RECORD_FORMAT, kind: 'chunk', index, params };
  }
  return null;
}

// ---------------------------------------------------------------------------
// 对比
// ---------------------------------------------------------------------------

/**
 * 一条快照差异。
 *
 * **名字必须与 `params` 里的 `ParamDiffEntry` 不同，不能"合并成一个类型"。**
 *
 * 两者看起来都是"一条参数差异"，但方向是反的：这里的 `base` → `next` 是
 * 「基准（原厂默认）→ 快照」，而 `params/file.ts` 的 `reference` → `current` 是
 * 「参考值（要导入的文件）→ 机体当前值」。方向反了，`added` / `removed` 的含义就跟着反：
 * 同一个"快照里有、基准里没有"的参数，在那边会被标成 `removed`。
 * 合并只能靠改掉一方的字段名**并**翻转它的 `kind` 标签，那是在用一个"统一"的假象
 * 掩盖两处真实的语义差异 —— 调用方会在两套方向之间反复犯错。
 *
 * 而重名这件事本身就有独立危害：`src/index.ts` 里两个 `export *` 撞上同名时，
 * ESM 会**静默排除**这个名字，于是 `import { ParamDiffEntry }` 类的问题会以
 * "找不到导出"的形式出现在使用处，完全看不出根因是重名。
 * 所以这里用 `Snapshot` 前缀把"这是快照对比的结果"写进名字里。
 */
export interface SnapshotDiffEntry {
  readonly name: string;
  readonly kind: 'changed' | 'added' | 'removed';
  /** 基准（通常是原厂默认）里的值；`added` 时为 null */
  readonly base: number | null;
  /** 当前值；`removed` 时为 null */
  readonly next: number | null;
}

export interface SnapshotDiffResult {
  readonly entries: readonly SnapshotDiffEntry[];
  readonly changed: number;
  readonly added: number;
  readonly removed: number;
  /** 两边都有的参数条数，用于界面显示"已对比 1032 / 1100" */
  readonly compared: number;
}

/**
 * 对比两份参数集。
 *
 * 用"名字"而不是索引位置对比：参数在集合里的顺序随下载批次变化，按位置比会把同一份参数
 * 报成满天飞的差异。浮点比较直接用 `!==`：两边是同一个 `float` 反序列化出来的 `number`，
 * 不需要容差；引入容差反而会吞掉真实的微小改动。
 */
export function diffParamSets(base: readonly ParamRecord[], next: readonly ParamRecord[]): SnapshotDiffResult {
  const baseByName = new Map<string, number>();
  for (const record of base) baseByName.set(record.name, record.value);
  const nextByName = new Map<string, number>();
  for (const record of next) nextByName.set(record.name, record.value);

  const entries: SnapshotDiffEntry[] = [];
  let changed = 0;
  let added = 0;
  let removed = 0;
  let compared = 0;

  for (const [name, nextValue] of nextByName) {
    const baseValue = baseByName.get(name);
    if (baseValue === undefined) {
      added += 1;
      entries.push({ name, kind: 'added', base: null, next: nextValue });
      continue;
    }
    compared += 1;
    if (baseValue !== nextValue) {
      changed += 1;
      entries.push({ name, kind: 'changed', base: baseValue, next: nextValue });
    }
  }

  for (const [name, baseValue] of baseByName) {
    if (nextByName.has(name)) continue;
    removed += 1;
    entries.push({ name, kind: 'removed', base: baseValue, next: null });
  }

  entries.sort((left, right) => (left.name < right.name ? -1 : left.name > right.name ? 1 : 0));
  return { entries, changed, added, removed, compared };
}

// ---------------------------------------------------------------------------
// 仓库
// ---------------------------------------------------------------------------

export interface ParamSnapshotRepositoryOptions {
  readonly now?: () => number;
  /** 失败时的旁路通知（界面用它弹一次提示）。仓储本身仍然用返回值报错 */
  readonly onFailure?: (failure: StorageFailure) => void;
}

export interface SaveParamSnapshotInit {
  readonly model: string;
  readonly firmware: string;
  readonly params: readonly ParamRecord[];
  readonly slot?: ParamSnapshotSlot;
  readonly source?: ParamSnapshotSource;
  readonly capturedAt?: number;
  readonly vehicleLabel?: string;
  readonly notes?: string;
}

export class ParamSnapshotRepository {
  private readonly store: KeyValueStore;
  private readonly now: () => number;
  private readonly onFailure: ((failure: StorageFailure) => void) | undefined;

  constructor(store: KeyValueStore, options: ParamSnapshotRepositoryOptions = {}) {
    this.store = store;
    this.now = options.now ?? Date.now;
    this.onFailure = options.onFailure;
  }

  /** 存档一份参数快照；同机型同固件同槽位的旧快照会被完整替换（含多余的旧分片） */
  async save(init: SaveParamSnapshotInit): Promise<SaveOutcome<ParamSnapshotSummary>> {
    return this.attempt('保存参数快照', async () => {
      for (const record of init.params) {
        if (!isParamRecord(record)) {
          // 名字超长/非字符串这类问题走 assertStorable 的报错路径，而不是写进去再读不出来
          throw new NotStorableError(
            `param-snapshots/${init.model}`,
            `参数名必须是 1..16 个字符的字符串且值为数字，收到 ${JSON.stringify(record)}`,
          );
        }
      }
      const slot = init.slot ?? 'last';
      const built = buildParamSnapshot({
        ...init,
        slot,
        capturedAt: init.capturedAt ?? this.now(),
      });

      const operations: StoreOperation[] = [
        {
          kind: 'put',
          collection: 'param-snapshots',
          key: paramSnapshotChunkKey(built.header.model, built.header.firmware, slot, 0),
          value: built.header,
        },
      ];
      for (const chunk of built.chunks) {
        operations.push({
          kind: 'put',
          collection: 'param-snapshots',
          key: paramSnapshotChunkKey(built.header.model, built.header.firmware, slot, chunk.index),
          value: chunk,
        });
      }

      // 旧快照比新快照长时，多余的片必须一起删：否则"上一次是 1500 条"的尾巴会被留在库里，
      // 头部已经被新的 1200 条覆盖，拼出来的参数集是错的且没有任何报错
      const existing = await this.store.keys('param-snapshots', paramSnapshotSlotPrefix(built.header.model, built.header.firmware, slot));
      for (const key of existing) {
        const info = parseParamSnapshotKey(key);
        if (info === null || info.chunkIndex <= built.chunks.length) continue;
        operations.push({ kind: 'delete', collection: 'param-snapshots', key });
      }

      await this.store.batch(operations);
      return saveOk(summaryOf(built.header));
    });
  }

  /** 只读头部：列表与"上次的样子"摘要只需要一条记录，不必解码全部参数 */
  async header(model: string, firmware: string, slot: ParamSnapshotSlot = 'last'): Promise<ParamSnapshotSummary | null> {
    const raw = await this.store.get('param-snapshots', paramSnapshotChunkKey(model, firmware, slot, 0));
    const record = readParamSnapshotRecord(raw);
    if (record === null || record.kind !== 'header') return null;
    return summaryOf(record);
  }

  /** 读回整份快照。缺片时**不猜**：返回 null，让界面显示"这份快照不完整"而不是半份参数 */
  async latest(model: string, firmware: string, slot: ParamSnapshotSlot = 'last'): Promise<ParamSnapshot | null> {
    const headerRaw = await this.store.get('param-snapshots', paramSnapshotChunkKey(model, firmware, slot, 0));
    const header = readParamSnapshotRecord(headerRaw);
    if (header === null || header.kind !== 'header') return null;

    const keys = await this.store.keys('param-snapshots', paramSnapshotSlotPrefix(model, firmware, slot));
    const chunks = new Map<number, ParamSnapshotChunk>();
    for (const key of keys) {
      const info = parseParamSnapshotKey(key);
      if (info === null || info.chunkIndex === 0) continue;
      const raw = await this.store.get('param-snapshots', key);
      const record = readParamSnapshotRecord(raw);
      if (record === null || record.kind !== 'chunk') continue;
      chunks.set(record.index, record);
    }
    if (chunks.size !== header.chunkCount) return null;

    const params: ParamRecord[] = [];
    for (let index = 1; index <= header.chunkCount; index += 1) {
      const chunk = chunks.get(index);
      if (chunk === undefined) return null;
      params.push(...chunk.params);
    }
    if (params.length !== header.paramCount) return null;
    return { header, params };
  }

  /** 列出所有机型/固件/槽位的头部；不需要读任何参数分片 */
  async list(): Promise<readonly ParamSnapshotSummary[]> {
    const keys = await this.store.keys('param-snapshots', PARAM_SNAPSHOT_PREFIX);
    const summaries: ParamSnapshotSummary[] = [];
    for (const key of keys) {
      const info = parseParamSnapshotKey(key);
      if (info === null || info.chunkIndex !== 0) continue;
      const raw = await this.store.get('param-snapshots', key);
      const record = readParamSnapshotRecord(raw);
      if (record === null || record.kind !== 'header') continue;
      summaries.push(summaryOf(record));
    }
    summaries.sort((left, right) => {
      if (left.capturedAt !== right.capturedAt) return right.capturedAt - left.capturedAt;
      if (left.model !== right.model) return left.model < right.model ? -1 : 1;
      if (left.firmware !== right.firmware) return left.firmware < right.firmware ? -1 : 1;
      return left.slot < right.slot ? -1 : left.slot > right.slot ? 1 : 0;
    });
    return summaries;
  }

  /** 删除某一个槽位的全部记录（头部 + 分片） */
  async remove(model: string, firmware: string, slot: ParamSnapshotSlot = 'last'): Promise<SaveOutcome<number>> {
    return this.attempt('删除参数快照', async () => {
      const keys = await this.store.keys('param-snapshots', paramSnapshotSlotPrefix(model, firmware, slot));
      if (keys.length === 0) return saveOk(0);
      await this.store.batch(keys.map((key) => ({ kind: 'delete', collection: 'param-snapshots', key }) as const));
      return saveOk(keys.length);
    });
  }

  /** 在同一机型/固件下对比两个槽位（典型用法：`'factory-default'` 对 `'last'`） */
  async diffSlots(
    model: string,
    firmware: string,
    baseSlot: ParamSnapshotSlot,
    nextSlot: ParamSnapshotSlot,
  ): Promise<SnapshotDiffResult | null> {
    const base = await this.latest(model, firmware, baseSlot);
    const next = await this.latest(model, firmware, nextSlot);
    if (base === null || next === null) return null;
    return diffParamSets(base.params, next.params);
  }

  private async attempt<T>(operation: string, work: () => Promise<SaveOk<T>>): Promise<SaveOutcome<T>> {
    const outcome = await runSave(operation, work, { collection: 'param-snapshots' });
    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }
}

function summaryOf(header: ParamSnapshotHeader): ParamSnapshotSummary {
  return {
    model: header.model,
    firmware: header.firmware,
    slot: header.slot,
    source: header.source,
    capturedAt: header.capturedAt,
    paramCount: header.paramCount,
    chunkSize: header.chunkSize,
    chunkCount: header.chunkCount,
    bytes: header.bytes,
    ...(header.vehicleLabel === undefined ? {} : { vehicleLabel: header.vehicleLabel }),
    ...(header.notes === undefined ? {} : { notes: header.notes }),
  };
}

/** 迁移用：读旧格式记录时把它归一化成新的头部 + 分片 */
export function normalizeLegacyParamSet(
  legacy: unknown,
): { readonly params: readonly ParamRecord[]; readonly capturedAt: number } | null {
  if (!isRecordLike(legacy)) return null;
  const params = legacy['params'];
  const capturedAt = readFiniteNumber(legacy['capturedAt']);
  const at = capturedAt ?? readFiniteNumber(legacy['at']) ?? 0;
  if (typeof params === 'string') {
    // 早期版本把参数存成 JSON 字符串（曾经尝试过更小的体积）。解析失败就是真的坏了，
    // 交给调用方报告为"不可迁移"，而不是把它当成空参数集写进去 —— 那等于静默丢数据
    try {
      const parsed: unknown = JSON.parse(params);
      if (!Array.isArray(parsed)) return null;
      return parseParamList(parsed, at);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(params)) return null;
  return parseParamList(params, at);
}

function parseParamList(list: readonly unknown[], at: number): { readonly params: readonly ParamRecord[]; readonly capturedAt: number } | null {
  const params: ParamRecord[] = [];
  for (const entry of list) {
    if (!isParamRecord(entry)) return null;
    params.push({ name: entry.name, value: entry.value });
  }
  return { params, capturedAt: at };
}
